/**
 * Anomali kuyruğu — KK-806.
 *
 * =============================================================================
 * NOKTA FARKINDALIĞI EKRANDA DA ANLATILIR
 * -----------------------------------------------------------------------------
 * Aynı kartın gün içinde FARKLI NOKTALARDA görünmesi NORMALDİR: öğrenci
 * kantinden simit, spor salonu büfesinden su alır. Anomali olan, aynı kartın
 * AYNI NOKTADA iki farklı terminalde kısa sürede görünmesidir — bu klonlama
 * veya başkasının kartını kullanma sinyalidir.
 *
 * Bu ayrım yalnızca sunucu kuralında kalırsa, panelde "çok noktalı kullanım"
 * satırını gören müdür öğrenciyi haksız yere sorgular. Bu yüzden ayrım
 * EKRANIN ÜSTÜNDE ve HER KURALIN YANINDA yazılıdır; şiddet rozetleri de bunu
 * destekler (çok noktalı kullanım ZAYIF sinyaldir, tek başına suçlayıcı değil).
 * =============================================================================
 */
import { useMemo, useState } from "react";
import { Buton, Kart, Rozet, Uyari } from "@kantin/ui";
import type { RozetTuru } from "@kantin/ui";
import { oturumDurumu } from "../../veri/oturum.js";
import { useAnomali, useAnomaliIsaretle } from "../../veri/sorgular.js";
import type { Anomali as AnomaliKaydi } from "../../veri/tipler.js";
import {
  ANOMALI_KURAL_ADI,
  SIDDET_ADI,
  detayAciklamasi,
  jsonGoster,
  kisiAdi,
  tarihSaat,
  yaslanma,
} from "../../veri/bicim.js";
import {
  FiltreSeridi,
  IslemDurumu,
  Sayfa,
  SayfaBasligi,
  Sayfalayici,
  Sekmeler,
  SorguDurumu,
} from "../../ortak.js";

type Gorunum = "bekleyen" | "incelenen" | "tumu";

const GORUNUM_INCELENDI: Record<Gorunum, boolean | null> = {
  bekleyen: false,
  incelenen: true,
  tumu: null,
};

const SIDDET_ROZETI: Record<number, RozetTuru> = {
  1: "notr",
  2: "uyari",
  3: "hata",
};

/** Kural başına "bu nedir, ne kadar ciddidir" açıklaması. */
const KURAL_ACIKLAMASI: Record<string, string> = {
  ayni_nokta_iki_terminal:
    "Aynı kart AYNI NOKTADA iki farklı terminalde 60 saniyeden kısa sürede göründü. Klonlama veya kart ödünç verme sinyali — en yüksek şiddet.",
  harcama_sicramasi:
    "Günlük harcama, kişinin 30 günlük ortalamasının belirgin üzerine çıktı.",
  ardisik_iptal:
    "Aynı ürün arka arkaya çok sayıda iptal edildi; kasa tarafında bir yanlışlık ya da kötüye kullanım olabilir.",
  cok_noktali_kullanim:
    "Tek kart gün içinde 3+ FARKLI noktada kullanıldı. Bu tek başına anomali DEĞİLDİR; zayıf bir sinyaldir ve ancak başka bulgularla birlikte anlam kazanır.",
};

export function Anomali() {
  const seciliNoktaId = oturumDurumu((s) => s.seciliNoktaId);
  const [gorunum, setGorunum] = useState<Gorunum>("bekleyen");
  const [siddet, setSiddet] = useState("");
  const [sayfa, setSayfa] = useState(1);

  const sorgu = useAnomali(
    GORUNUM_INCELENDI[gorunum],
    siddet ? Number.parseInt(siddet, 10) : null,
    sayfa,
  );

  const kayitlar = useMemo(() => {
    const hepsi = sorgu.data?.anomaliler ?? [];
    if (!seciliNoktaId) return hepsi;
    return hepsi.filter((a) => a.nokta_id === seciliNoktaId);
  }, [sorgu.data, seciliNoktaId]);

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Anomaliler"
        aciklama="Nokta farkındalıklı kuralların ürettiği bulgular. İnceleyip işaretleyin; kayıt silinmez, denetim izinde kalır."
      />

      <Uyari tur="bilgi" baslik="Nokta farkındalığı">
        Aynı kartın <strong>farklı noktalarda</strong> görünmesi normaldir
        (öğrenci kantinden simit, büfeden su alır) ve alarm üretmez. Anomali
        olan, aynı kartın <strong>aynı noktada iki terminalde</strong> kısa
        sürede görünmesidir.
      </Uyari>

      <Sekmeler<Gorunum>
        etiket="Anomali görünümü"
        secili={gorunum}
        sec={(y) => {
          setGorunum(y);
          setSayfa(1);
        }}
        sekmeler={[
          { deger: "bekleyen", metin: "İncelenmemiş" },
          { deger: "incelenen", metin: "İncelenmiş" },
          { deger: "tumu", metin: "Tümü" },
        ]}
      />

      <FiltreSeridi>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="anomali-siddet"
            className="text-sm font-semibold text-murekkep-2"
          >
            Şiddet
          </label>
          <select
            id="anomali-siddet"
            value={siddet}
            onChange={(e) => {
              setSiddet(e.target.value);
              setSayfa(1);
            }}
            className="min-h-hedef rounded-lg border border-cetvel bg-yuzey px-3 font-govde text-md text-murekkep"
          >
            <option value="">Tüm şiddetler</option>
            <option value="3">Güçlü</option>
            <option value="2">Orta</option>
            <option value="1">Zayıf</option>
          </select>
        </div>
        {seciliNoktaId && (
          <p className="m-0 max-w-prose text-xs text-murekkep-3" role="status">
            Nokta süzgeci sunucuda değil, açık sayfada uygulanır: bu sayfadaki{" "}
            <span className="rakam font-mono">
              {sorgu.data?.anomaliler.length ?? 0}
            </span>{" "}
            kayıttan <span className="rakam font-mono">{kayitlar.length}</span>{" "}
            tanesi gösteriliyor.
          </p>
        )}
      </FiltreSeridi>

      <SorguDurumu
        yukleniyor={sorgu.isLoading}
        hata={sorgu.error}
        bos={!sorgu.isLoading && kayitlar.length === 0}
        bosBaslik="Anomali yok"
        bosAciklama="Bu süzgeçte incelenecek bulgu bulunmuyor."
        iskeletSatir={5}
        yenile={() => void sorgu.refetch()}
      >
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {kayitlar.map((a) => (
            <li key={a.id}>
              <AnomaliKarti kayit={a} />
            </li>
          ))}
        </ul>

        <Sayfalayici
          sayfa={sorgu.data?.sayfa ?? 1}
          boyut={sorgu.data?.sayfa_boyu ?? 50}
          toplam={sorgu.data?.toplam ?? 0}
          degistir={setSayfa}
        />
      </SorguDurumu>
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function AnomaliKarti({ kayit }: { kayit: AnomaliKaydi }) {
  const isaretle = useAnomaliIsaretle();
  const aciklama =
    detayAciklamasi(kayit.detay) ?? KURAL_ACIKLAMASI[kayit.kural] ?? null;

  return (
    <Kart
      baslik={
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="m-0 text-lg">
            {ANOMALI_KURAL_ADI[kayit.kural] ?? kayit.kural}
          </h2>
          <Rozet tur={SIDDET_ROZETI[kayit.siddet] ?? "notr"}>
            {SIDDET_ADI[kayit.siddet] ?? `Şiddet ${kayit.siddet}`}
          </Rozet>
          {kayit.incelendi && <Rozet tur="onay">incelendi</Rozet>}
        </div>
      }
      ustBilgi={
        <span className="text-sm text-murekkep-3">
          {yaslanma(kayit.olusma_zamani)} önce
        </span>
      }
    >
      <div className="flex flex-col gap-2 text-sm">
        <p className="m-0">
          <span className="text-murekkep-3">Kişi:</span>{" "}
          <strong className="text-murekkep">{kisiAdi(kayit.kisi)}</strong>
          {kayit.nokta_ad && (
            <>
              {" "}
              · <span className="text-murekkep-3">Nokta:</span>{" "}
              <strong className="text-murekkep">{kayit.nokta_ad}</strong>
            </>
          )}
        </p>
        <p className="m-0">
          <span className="text-murekkep-3">Zaman:</span>{" "}
          <span className="rakam font-mono">
            {tarihSaat(kayit.olusma_zamani)}
          </span>
        </p>

        {aciklama && (
          <p className="m-0 max-w-prose text-murekkep">{aciklama}</p>
        )}

        {kayit.kural === "cok_noktali_kullanim" && (
          <Uyari tur="bilgi">
            Bu kural ZAYIF sinyaldir: farklı noktalarda alışveriş normaldir. Tek
            başına bir suçlama gerekçesi değildir.
          </Uyari>
        )}

        <details className="rounded-md border border-cetvel bg-kagit-2 p-2">
          <summary className="cursor-pointer text-xs text-murekkep-2">
            Ham bulgu ayrıntısı
          </summary>
          <pre className="m-0 mt-2 overflow-x-auto text-xs text-murekkep-2">
            {jsonGoster(kayit.detay)}
          </pre>
        </details>

        <div className="flex flex-wrap items-center gap-3">
          <Buton
            tur={kayit.incelendi ? "sessiz" : "onay"}
            yukleniyor={isaretle.isPending}
            onClick={() =>
              isaretle.mutate({ id: kayit.id, incelendi: !kayit.incelendi })
            }
          >
            {kayit.incelendi
              ? "İncelendi işaretini kaldır"
              : "İncelendi olarak işaretle"}
          </Buton>
        </div>

        <IslemDurumu hata={isaretle.error} />
      </div>
    </Kart>
  );
}
